'use client'

import { Check, ChevronDown, ChevronRight, Loader2, X } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { ActionCard, type ActionCardTone } from './action-card'
import { Button } from './button'

/**
 * PlanCard — multi-step composition. The headline awaiting-input card.
 *
 * The agent emits a PlanCard when it has 2+ tightly-coupled actions
 * to propose ("draft the email, schedule the meeting, file the doc").
 * Each step is a collapsible row with slug + summary + optional args
 * preview. The user picks one of:
 *   - Run all: execute steps sequentially
 *   - Run step N only: execute one step, hold the rest
 *   - Edit step N: open that step's underlying card to tweak args
 *   - Cancel: discard the whole plan
 *
 * Per-step state (queued/running/done/error/skipped) is updated by the
 * host while the plan executes; the visual reflects step status so the
 * user can watch the plan unfold.
 *
 * Per `apps/agent/plans/plan-action-cards.md` Phase 5.
 */

export type PlanStepState = 'queued' | 'running' | 'done' | 'error' | 'skipped'

export interface PlanStep {
  id: string
  toolSlug: string
  summary: string
  args?: unknown
  dependsOn?: number[]
  state?: PlanStepState
}

export type PlanAction =
  | { action: 'runAll' }
  | { action: 'runStep'; stepIndex: number }
  | { action: 'editStep'; stepIndex: number; editedArgs?: unknown }
  | { action: 'cancel' }

export interface PlanCardProps {
  title: string
  description?: string
  attribution?: string
  steps: PlanStep[]
  runAllLabel?: string
  cancelLabel?: string
  /**
   * If set, lock the card and show step states without action buttons.
   * Used for transcript replay after the plan ran.
   */
  resolved?: boolean
  onAction?: (action: PlanAction) => void
}

const STATE_DOT: Record<PlanStepState, string> = {
  queued: 'bg-muted-foreground/30',
  running: 'bg-amber-500 animate-pulse',
  done: 'bg-emerald-500',
  error: 'bg-destructive',
  skipped: 'bg-muted-foreground/20',
}

const STATE_LABEL: Record<PlanStepState, string> = {
  queued: 'Queued',
  running: 'Running…',
  done: 'Done',
  error: 'Error',
  skipped: 'Skipped',
}

export function PlanCard({
  title,
  description,
  attribution,
  steps,
  runAllLabel = 'Run all',
  cancelLabel = 'Cancel',
  resolved,
  onAction,
}: PlanCardProps) {
  // Auto-expand short plans (≤3 steps); collapse when 4+. The user can
  // override per-row via the chevron.
  const [expanded, setExpanded] = React.useState<Set<string>>(
    () => new Set(steps.length <= 3 ? steps.map((s) => s.id) : []),
  )
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })

  const allDone = steps.every((s) => s.state === 'done')
  const anyError = steps.some((s) => s.state === 'error')
  const anyRunning = steps.some((s) => s.state === 'running')
  const tone: ActionCardTone = resolved ? (allDone ? 'ok' : anyError ? 'destructive' : 'neutral') : 'pending'

  return (
    <ActionCard
      title={title}
      description={description}
      attribution={attribution}
      tone={tone}
      footer={
        resolved ? null : (
          <>
            <Button type="button" size="sm" disabled={anyRunning} onClick={() => onAction?.({ action: 'runAll' })}>
              {runAllLabel}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => onAction?.({ action: 'cancel' })}>
              {cancelLabel}
            </Button>
          </>
        )
      }>
      <ol className="flex flex-col gap-2">
        {steps.map((step, i) => {
          const isExpanded = expanded.has(step.id)
          const state = step.state ?? 'queued'
          const Chevron = isExpanded ? ChevronDown : ChevronRight
          return (
            <li
              key={step.id}
              className={cn(
                'rounded-md border border-border/40 bg-background',
                state === 'running' && 'border-amber-300 dark:border-amber-500/40',
                state === 'done' && 'border-emerald-300 dark:border-emerald-500/40',
                state === 'error' && 'border-destructive/40',
              )}>
              <button
                type="button"
                onClick={() => toggle(step.id)}
                className="flex w-full items-start gap-2 px-2.5 py-1.5 text-left">
                <span className={cn('mt-1 inline-block h-2 w-2 shrink-0 rounded-full', STATE_DOT[state])} aria-hidden />
                <span className="grow text-xs">
                  <span className="text-muted-foreground">{i + 1}.</span>{' '}
                  <span className="font-mono text-[11px] text-muted-foreground">{step.toolSlug}</span>
                  <span className="ml-2 text-foreground">{step.summary}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1 text-[10.5px] text-muted-foreground">
                  {state === 'running' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
                  {state === 'done' ? <Check className="h-3 w-3 text-emerald-500" aria-hidden /> : null}
                  {state === 'error' ? <X className="h-3 w-3 text-destructive" aria-hidden /> : null}
                  <span>{STATE_LABEL[state]}</span>
                  <Chevron className="h-3 w-3 opacity-60" aria-hidden />
                </span>
              </button>
              {isExpanded ? (
                <div className="border-t border-border/40 px-2.5 py-2">
                  {step.args !== undefined ? (
                    <pre className="max-h-32 overflow-auto rounded bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
                      {typeof step.args === 'string' ? step.args : JSON.stringify(step.args, null, 2)}
                    </pre>
                  ) : null}
                  {!resolved ? (
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={state === 'running' || state === 'done'}
                        onClick={() => onAction?.({ action: 'runStep', stepIndex: i })}>
                        Run this step only
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={state === 'running' || state === 'done'}
                        onClick={() => onAction?.({ action: 'editStep', stepIndex: i })}>
                        Edit
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </li>
          )
        })}
      </ol>
    </ActionCard>
  )
}
